import { FC, Fragment, useReducer, useState } from 'react';

import clsx from 'clsx';
import { Gem, IdCard, Settings, ShieldBan, ShieldCheck, SquareCheck, UserMinus, Users, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import { ConfigureBalanceDialog } from './configureBalanceDialog/ConfigureBalanceDialog';
import { DeleteUserDialog } from './deleteUserDialog/DeleteUserDialog';
import { EditTasksDialog } from './editTasksDialog/EditTasksDialog';
import { PayOutDialog } from './payOutDialog/PayOutDialog';
import { ReferralsDialog } from './referralsDialog/ReferralsDialog';
import { TemporalIdDialog } from './temporalIdDialog/TemporalIdDialog';

import css from './UserSettingsActionsColumn.module.css';

import { userService } from 'v2/api/userService';
import { Nullable } from 'v2/types';
import { UserModel } from 'v2/types/UserModel';
import { handleHttpError } from 'v2/utils';

interface UserSettingsActionsColumnProps {
  user: UserModel;
  isUserHeadSupport: boolean;
  onRevalidate: VoidFunction;
}

export const UserSettingsActionsColumn: FC<UserSettingsActionsColumnProps> = ({
  user,
  onRevalidate,
  isUserHeadSupport,
}) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);

  const [isOpenPayOutDialog, setOpenPayOutDialog] = useReducer((state) => !state, false);
  const [isOpenDeleteUserDialog, setOpenDeleteUserDialog] = useReducer((state) => !state, false);
  const [isOpenTemporalIdDialog, setOpenTemporalIdDialog] = useReducer((state) => !state, false);
  const [isOpenBalanceDialog, setOpenBalanceDialog] = useReducer((state) => !state, false);
  const [isOpenEditTasksDialog, setOpenEditTasksDialog] = useReducer((state) => !state, false);
  const [isOpenReferralsDialog, setOpenReferralsDialog] = useReducer((state) => !state, false);

  const { trigger } = useSWRMutation(['updateUser', user.id], userService.updateUser, {
    onSuccess: (user) => {
      onRevalidate();
      toast.success(`User ${user.userNickname} was ${user.isBanned ? 'banned' : 'unbanned'}`);
    },
    onError: handleHttpError,
  });

  const handleBan = () => {
    trigger({ isBanned: !user.isBanned });
  };

  const handleMenuItemClose = (cb: VoidFunction) => () => {
    handleClose();
    cb();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className={css.userSettingsActionsColumn}>
      <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
        <Settings width={22} height={22} />
      </IconButton>

      <Tooltip title={user.isBanned ? 'Unban' : 'Ban'} placement="top">
        <IconButton color={user.isBanned ? 'success' : 'error'} onClick={handleBan}>
          {user.isBanned ? <ShieldCheck width={23} height={23} /> : <ShieldBan width={22} height={22} />}
        </IconButton>
      </Tooltip>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {!isUserHeadSupport && (
          <MenuItem className={css.userSettingsActionsMenuItem} onClick={handleMenuItemClose(setOpenPayOutDialog)}>
            <Gem width={18} height={18} /> Pay Out Diamonds
          </MenuItem>
        )}

        <Divider />

        <MenuItem className={css.userSettingsActionsMenuItem} onClick={handleMenuItemClose(setOpenBalanceDialog)}>
          <Wallet width={18} height={18} /> Configure Balance
        </MenuItem>

        {!isUserHeadSupport && (
          <Fragment>
            <Divider />

            <MenuItem
              className={css.userSettingsActionsMenuItem}
              onClick={handleMenuItemClose(setOpenTemporalIdDialog)}
            >
              <IdCard width={19} height={19} /> Get Temporal Id
            </MenuItem>
          </Fragment>
        )}

        <Divider />

        <MenuItem className={css.userSettingsActionsMenuItem} onClick={handleMenuItemClose(setOpenEditTasksDialog)}>
          <SquareCheck width={18} height={18} /> Edit Tasks
        </MenuItem>

        <Divider />

        <MenuItem className={css.userSettingsActionsMenuItem} onClick={handleMenuItemClose(setOpenReferralsDialog)}>
          <Users width={18} height={18} /> Referrals
        </MenuItem>

        {!isUserHeadSupport && (
          <Fragment>
            <Divider />

            <MenuItem
              className={clsx(css.userSettingsActionsMenuItem, css.danger)}
              onClick={handleMenuItemClose(setOpenDeleteUserDialog)}
            >
              <UserMinus width={18} height={18} /> Delete User
            </MenuItem>
          </Fragment>
        )}
      </Menu>

      <PayOutDialog user={user} isOpen={isOpenPayOutDialog} onClose={setOpenPayOutDialog} onRevalidate={onRevalidate} />

      <ConfigureBalanceDialog user={user} isOpen={isOpenBalanceDialog} onClose={setOpenBalanceDialog} />

      <TemporalIdDialog user={user} isOpen={isOpenTemporalIdDialog} onClose={setOpenTemporalIdDialog} />

      <EditTasksDialog
        user={user}
        isOpen={isOpenEditTasksDialog}
        onClose={setOpenEditTasksDialog}
        onRevalidate={onRevalidate}
      />

      <ReferralsDialog user={user} isOpen={isOpenReferralsDialog} onClose={setOpenReferralsDialog} />

      <DeleteUserDialog
        user={user}
        isOpen={isOpenDeleteUserDialog}
        onClose={setOpenDeleteUserDialog}
        onRevalidate={onRevalidate}
      />
    </div>
  );
};
